import React, { useState, useEffect } from 'react';
import { Swords, RefreshCw, Trophy, ThumbsUp, Sparkles, Crown } from 'lucide-react';
import apiService from '../services/api';

// AI avatars/colors
const AI_CONFIG = {
  'Claude': { color: 'from-orange-500 to-amber-500', emoji: '🧠', bg: 'bg-orange-50 dark:bg-orange-900/20' },
  'GPT': { color: 'from-green-500 to-emerald-500', emoji: '🤖', bg: 'bg-green-50 dark:bg-green-900/20' },
  'Gemini': { color: 'from-blue-500 to-cyan-500', emoji: '💎', bg: 'bg-blue-50 dark:bg-blue-900/20' },
  'Groq': { color: 'from-purple-500 to-pink-500', emoji: '⚡', bg: 'bg-purple-50 dark:bg-purple-900/20' },
  'Grok': { color: 'from-gray-700 to-black', emoji: '𝕏', bg: 'bg-gray-50 dark:bg-gray-800/50' },
  'Mistral': { color: 'from-orange-600 to-red-600', emoji: '🌀', bg: 'bg-red-50 dark:bg-red-900/20' },
  'DeepSeek': { color: 'from-cyan-600 to-blue-700', emoji: '🔮', bg: 'bg-cyan-50 dark:bg-cyan-900/20' },
  // 'Perplexity': { color: 'from-teal-500 to-emerald-600', emoji: '🔍', bg: 'bg-teal-50 dark:bg-teal-900/20' },  // disabled - refuses creative prompts
};

// Thinking indicator component - Compact
const ThinkingIndicator = ({ ai }) => {
  const config = AI_CONFIG[ai] || AI_CONFIG['Claude'];
  return (
    <div className={`flex items-center gap-2 p-3 rounded-xl ${config.bg} border border-gray-200 dark:border-gray-700 h-full`}>
      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${config.color} flex items-center justify-center text-base shadow`}>
        {config.emoji}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-600 dark:text-gray-400">{ai}</span>
        <span className="flex gap-0.5 ml-1">
          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
        </span>
      </div>
    </div>
  );
};

// Quote card component - Updated for v2 (compact grid cards)
const QuoteCard = ({ item, voted, onVote, visible, pickedType, pickedTheme, nerdPick }) => {
  const config = AI_CONFIG[item.ai] || AI_CONFIG['Claude'];
  const isWinner = voted && voted === item.ai;
  const isNerdPick = voted && nerdPick === item.ai;

  if (!visible) {
    return <ThinkingIndicator ai={item.ai} />;
  }

  // Determine border styling
  const getBorderClass = () => {
    if (isWinner && isNerdPick) {
      // Both picked this one - gold + teal
      return 'border-yellow-400 ring-2 ring-yellow-200 dark:ring-yellow-800';
    } else if (isWinner) {
      return 'border-yellow-400 ring-2 ring-yellow-200 dark:ring-yellow-800';
    } else if (isNerdPick) {
      return 'border-teal-400 ring-2 ring-teal-200 dark:ring-teal-800';
    } else if (voted) {
      return 'opacity-60 border-gray-200 dark:border-gray-700';
    }
    return 'border-transparent hover:border-purple-300 dark:hover:border-purple-600 cursor-pointer';
  };

  return (
    <div
      className={`relative rounded-xl shadow-md p-3 border-2 transition-all duration-500 animate-fadeIn h-full flex flex-col ${
        config.bg
      } ${getBorderClass()}`}
      onClick={() => !voted && onVote(item.ai)}
    >
      {/* Winner Crown - user pick */}
      {isWinner && (
        <div className="absolute -top-2 -right-2">
          <Crown className="h-6 w-6 text-yellow-500 fill-yellow-400 drop-shadow-lg" />
        </div>
      )}
      {/* Nerd Pick badge */}
      {isNerdPick && !isWinner && (
        <div className="absolute -top-2 -right-2">
          <span className="text-xl drop-shadow-lg">🤓</span>
        </div>
      )}
      {/* Both picked - show both badges */}
      {isWinner && isNerdPick && (
        <div className="absolute -top-2 -left-2">
          <span className="text-xl drop-shadow-lg">🤓</span>
        </div>
      )}

      {/* AI Header - Compact */}
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${config.color} flex items-center justify-center text-base shadow`}>
          {config.emoji}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm text-gray-800 dark:text-gray-200">{item.ai}</h3>
          {pickedType && (
            <span className="text-[10px] text-purple-600 dark:text-purple-400 font-medium">Type</span>
          )}
          {pickedTheme && (
            <span className="text-[10px] text-orange-600 dark:text-orange-400 font-medium">Theme</span>
          )}
        </div>
        {!voted && (
          <button className={`px-2 py-1 bg-gradient-to-r ${config.color} text-white rounded-full font-medium text-[10px] shadow hover:shadow-md transform hover:scale-105 transition-all flex items-center gap-1`}>
            <ThumbsUp className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Quote - Compact */}
      <div className="flex-1 relative">
        <p className="text-sm text-gray-700 dark:text-gray-300 italic leading-snug">
          "{item.quote}"
        </p>
      </div>
    </div>
  );
};

const SmartQuotesPage = () => {
  const [battle, setBattle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [voted, setVoted] = useState(null);
  const [totalBattles, setTotalBattles] = useState(0);

  // Staged reveal: 0=nothing, 1=type, 2=theme, 3=battling message, 4+=entries
  const [revealStage, setRevealStage] = useState(0);

  // Leaderboards from Redis (fetched on mount)
  const [leaderboard, setLeaderboard] = useState(
    { Claude: 0, GPT: 0, Gemini: 0, Groq: 0, Grok: 0, Mistral: 0, DeepSeek: 0 }
  );
  const [perplexityLeaderboard, setPerplexityLeaderboard] = useState(
    { Claude: 0, GPT: 0, Gemini: 0, Groq: 0, Grok: 0, Mistral: 0, DeepSeek: 0 }
  );

  // Fetch leaderboard from API on mount
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const data = await apiService.getQuoteBattleLeaderboard();
        if (data.leaderboard) {
          setLeaderboard(data.leaderboard);
          setTotalBattles(data.total_battles || 0);
        }
        if (data.perplexity_leaderboard) {
          setPerplexityLeaderboard(data.perplexity_leaderboard);
        }
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
      }
    };
    fetchLeaderboard();
  }, []);

  // Staged reveal animation - v2 format
  useEffect(() => {
    if (!battle || loading) return;

    // Start reveal sequence
    setRevealStage(0);

    // Stage 1: Show type (with pop animation)
    const t1 = setTimeout(() => setRevealStage(1), 100);

    // Stage 2: Show theme
    const t2 = setTimeout(() => setRevealStage(2), 1200);

    // Stage 3: Show "AIs are battling it out..." message
    const t3 = setTimeout(() => setRevealStage(3), 2200);

    // Stage 4+: Show each entry (use entries array for v2, fallback to old format)
    const entries = battle.entries || [battle.starter, ...(battle.responses || [])];
    const entryTimers = entries.map((_, i) =>
      setTimeout(() => setRevealStage(4 + i), 3200 + (i * 800))
    );

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      entryTimers.forEach(t => clearTimeout(t));
    };
  }, [battle, loading]);

  const startBattle = async () => {
    setBattle(null);
    setRevealStage(0);
    setLoading(true);
    setVoted(null);
    try {
      const response = await apiService.getSmartQuote();
      if (response.battle) {
        setBattle(response.battle);
        // Don't increment here - totalBattles is incremented when voting (in Redis)
      } else {
        setBattle({
          topic: 'wisdom',
          topic_by: 'Claude',
          starter: { ai: 'Claude', quote: response.quote },
          responses: []
        });
      }
    } catch (error) {
      console.error('Error starting battle:', error);
      const isTimeout = error.message?.includes('timed out');
      setBattle({
        topic: isTimeout ? 'cold start' : 'errors',
        topic_by: 'System',
        starter: {
          ai: 'Claude',
          quote: isTimeout
            ? "The AIs are still warming up... Try again in a moment!"
            : "I'm not arguing, I'm just explaining why I'm right."
        },
        responses: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (ai) => {
    if (voted) return;
    setVoted(ai);

    const perplexityPick = battle?.judge_pick;

    // Optimistically update UI
    setLeaderboard(prev => ({ ...prev, [ai]: (prev[ai] || 0) + 1 }));
    setTotalBattles(prev => prev + 1);
    if (perplexityPick) {
      setPerplexityLeaderboard(prev => ({ ...prev, [perplexityPick]: (prev[perplexityPick] || 0) + 1 }));
    }

    // Record vote in Redis via API (include Perplexity's pick)
    try {
      await apiService.recordQuoteBattleVote(ai, perplexityPick);
    } catch (error) {
      console.error('Error recording vote:', error);
      // Vote still counts locally even if API fails
    }
  };

  const getLeaderboardSorted = () => {
    return Object.entries(leaderboard)
      .sort(([, a], [, b]) => b - a)
      .map(([ai, wins], index) => ({ ai, wins, rank: index + 1 }));
  };

  const getPerplexityLeaderboardSorted = () => {
    return Object.entries(perplexityLeaderboard)
      .sort(([, a], [, b]) => b - a)
      .map(([ai, picks], index) => ({ ai, picks, rank: index + 1 }));
  };

  useEffect(() => {
    startBattle();
  }, []);

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 dark:from-gray-900 dark:via-purple-900 dark:to-gray-900">
      {/* Header with Leaderboards */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white p-4">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Title */}
          <div className="flex items-center gap-3 shrink-0">
            <Swords className="h-8 w-8" />
            <div>
              <h1 className="text-2xl font-bold">AI Quote Battle! ⚔️</h1>
              <p className="text-purple-100 text-sm">7 AIs compete. You be the judge!</p>
            </div>
          </div>

          {/* Center: Leaderboard Table + Battle Count */}
          <div className="hidden md:flex items-stretch gap-4">
            <div className="flex flex-col bg-white/10 rounded-xl px-4 py-2">
              {/* Header row: AI names */}
              <div className="flex items-center gap-1 mb-1">
                <div className="w-24"></div>
                {Object.keys(AI_CONFIG).map(ai => {
                  const config = AI_CONFIG[ai];
                  return (
                    <div key={ai} className="flex flex-col items-center w-14">
                      <span className="text-lg">{config.emoji}</span>
                      <span className="text-[10px] text-purple-200">{ai}</span>
                    </div>
                  );
                })}
              </div>

              {/* Human picks row */}
              <div className="flex items-center gap-1">
                <div className="flex items-center gap-1 w-24 shrink-0">
                  <Trophy className="h-5 w-5 text-yellow-300" />
                  <span className="text-sm font-medium text-yellow-200">Human</span>
                </div>
                {Object.keys(AI_CONFIG).map(ai => {
                  const wins = leaderboard[ai] || 0;
                  const isLeader = wins > 0 && wins === Math.max(...Object.values(leaderboard));
                  return (
                    <div
                      key={ai}
                      className={`flex items-center justify-center w-14 py-1 rounded ${
                        isLeader ? 'bg-yellow-400/30' : ''
                      }`}
                    >
                      <span className="text-lg font-bold">{wins}</span>
                    </div>
                  );
                })}
              </div>

              {/* AI Nerd picks row */}
              <div className="flex items-center gap-1">
                <div className="flex items-center gap-1 w-24 shrink-0">
                  <span className="text-lg">🤓</span>
                  <span className="text-sm font-medium text-teal-200">AI Nerd</span>
                </div>
                {Object.keys(AI_CONFIG).map(ai => {
                  const picks = perplexityLeaderboard[ai] || 0;
                  const isLeader = picks > 0 && picks === Math.max(...Object.values(perplexityLeaderboard));
                  return (
                    <div
                      key={ai}
                      className={`flex items-center justify-center w-14 py-1 rounded ${
                        isLeader ? 'bg-teal-400/30' : ''
                      }`}
                    >
                      <span className="text-lg font-bold">{picks}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Battle count - right next to leaderboard, same height */}
            <div className="flex items-center justify-center gap-2 bg-white/10 rounded-xl px-4">
              <Sparkles className="h-6 w-6 text-yellow-300" />
              <div className="text-center">
                <div className="text-3xl font-bold">{totalBattles}</div>
                <div className="text-sm text-purple-200">battles</div>
              </div>
            </div>
          </div>

          {/* Mobile: Compact leaderboard */}
          <div className="flex md:hidden flex-col gap-1">
            <div className="flex items-center gap-1 bg-white/10 rounded-lg px-2 py-1">
              <Trophy className="h-4 w-4 text-yellow-300" />
              <span className="text-xs text-yellow-200">Human</span>
              {getLeaderboardSorted().slice(0, 3).map(({ ai, wins, rank }) => (
                <div key={ai} className={`flex items-center gap-0.5 px-1 rounded ${rank === 1 ? 'bg-yellow-400/30' : ''}`}>
                  <span className="text-sm">{AI_CONFIG[ai]?.emoji}</span>
                  <span className="text-sm font-bold">{wins}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-1 bg-teal-500/20 rounded-lg px-2 py-1">
              <span className="text-sm">🤓</span>
              <span className="text-xs text-teal-200">Nerd</span>
              {getPerplexityLeaderboardSorted().slice(0, 3).map(({ ai, picks, rank }) => (
                <div key={ai} className={`flex items-center gap-0.5 px-1 rounded ${rank === 1 ? 'bg-teal-400/30' : ''}`}>
                  <span className="text-sm">{AI_CONFIG[ai]?.emoji}</span>
                  <span className="text-sm font-bold">{picks}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-1 text-sm">
              <Sparkles className="h-4 w-4 text-yellow-300" />
              <span className="font-bold">{totalBattles}</span>
              <span className="text-purple-200">battles</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        <div className="max-w-5xl mx-auto">

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center h-48">
              <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-purple-600 mb-3"></div>
              <p className="text-base text-gray-600 dark:text-gray-400 animate-pulse">Preparing the arena...</p>
            </div>
          )}

          {/* Type & Theme Banner - Same line */}
          {!loading && battle && revealStage >= 1 && (
            <div className="flex flex-wrap items-center justify-center gap-3 mb-4">
              {/* Type */}
              <div className="animate-topicPop text-center">
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-lg text-sm font-bold text-white">
                  <Sparkles className="h-4 w-4" />
                  <span className="uppercase tracking-wider">{battle.type || 'smart-ass quote'}</span>
                </span>
                {battle.type_by && (
                  <p className="text-gray-500 dark:text-gray-400 text-[10px] mt-0.5">by {battle.type_by}</p>
                )}
              </div>

              {/* Theme */}
              {revealStage >= 2 && (
                <div className="animate-topicPop text-center">
                  <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-orange-400 to-yellow-400 rounded-full shadow-lg text-sm font-bold text-white">
                    <span className="uppercase tracking-wider">{battle.theme || battle.topic}</span>
                  </span>
                  {battle.theme_by && (
                    <p className="text-gray-500 dark:text-gray-400 text-[10px] mt-0.5">by {battle.theme_by}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* "AIs are battling it out" message - Stage 3 */}
          {!loading && battle && revealStage === 3 && (
            <div className="flex items-center justify-center gap-3 p-4 my-4 animate-fadeIn">
              <Swords className="h-6 w-6 text-purple-500 animate-pulse" />
              <p className="text-lg text-gray-600 dark:text-gray-400 font-medium">AIs are battling it out...</p>
              <Swords className="h-6 w-6 text-purple-500 animate-pulse" />
            </div>
          )}

          {/* All Entries - before voting: show all, after voting: only show picks */}
          {!loading && battle && revealStage >= 3 && (
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              {(battle.entries || [battle.starter, ...(battle.responses || [])])
                .filter(entry => {
                  // After voting, only show user's pick and nerd's pick
                  if (!voted) return true;
                  return entry.ai === voted || entry.ai === battle?.judge_pick;
                })
                .map((entry, index) => (
                <div key={entry.ai} className={`w-full ${voted ? 'md:w-[calc(50%-0.375rem)]' : 'md:w-[calc(50%-0.375rem)] lg:w-[calc(33.333%-0.5rem)]'}`}>
                  <QuoteCard
                    item={entry}
                    voted={voted}
                    onVote={handleVote}
                    visible={voted || revealStage >= 4 + index}
                    pickedType={entry.ai === battle.type_by}
                    pickedTheme={entry.ai === battle.theme_by}
                    nerdPick={battle?.judge_pick}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Perplexity Fact Check - shown right after the picks */}
          {voted && battle?.judge_pick && (
            <div className="text-center mb-4 p-4 bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/30 dark:to-emerald-900/30 rounded-xl shadow-lg border-2 border-teal-200 dark:border-teal-700 animate-fadeIn max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-2xl">🤓</span>
                <span className="font-bold text-lg text-teal-700 dark:text-teal-300">Perplexity's Fact Check</span>
                <span className="text-2xl">📚</span>
              </div>
              <p className="text-lg text-gray-700 dark:text-gray-300">
                {voted === battle.judge_pick ? (
                  <span className="text-green-600 dark:text-green-400 font-medium">✓ The nerd agrees with your pick!</span>
                ) : (
                  <span className="text-orange-600 dark:text-orange-400 font-medium">The nerd picked differently!</span>
                )}
              </p>
              {battle.judge_reason && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 italic">
                  "{battle.judge_reason}"
                </p>
              )}
            </div>
          )}

          {/* Start New Battle Button - show after voting */}
          {!loading && battle && voted && (
            <div className="flex justify-center mb-4">
              <button
                onClick={startBattle}
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                <Swords className="h-5 w-5" />
                Next Battle!
              </button>
            </div>
          )}

          {/* Vote prompt - show after all revealed but before voting */}
          {!loading && battle && !voted && revealStage >= 4 + ((battle.entries || [battle.starter, ...(battle.responses || [])]).length - 1) && (
            <div className="text-center mb-4 p-2 animate-pulse">
              <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">
                👆 Click on your favorite to vote!
              </p>
            </div>
          )}

        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes topicPop {
          0% { opacity: 0; transform: scale(0.5); }
          50% { transform: scale(1.1); }
          70% { transform: scale(0.95); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes flash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
        .animate-topicPop {
          animation: topicPop 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>
    </div>
  );
};

export default SmartQuotesPage;
